import { buildMermaidInitializeConfig } from "@/src/lib/markdown/mermaid-config";
import { getFontStack } from "../fonts";
import type { ScriptFallbackKey } from "../script-fallbacks";
import { sanitizeCssColor } from "./escape";
import { getPageSizeValue } from "./page-geometry";
import { getPdfThemeDefinition } from "./themes";
import {
  mermaidScriptHref,
  pagedPreviewCompleteEvent,
  pagedPreviewFallbackEvent,
  pagedPreviewUpdateMessageType,
  type PdfOptions,
  type PrintDocumentVariant,
} from "./types";

export function buildPagedPreviewBootstrapScript(
  previewSurfaceBackground: string,
  options: PdfOptions,
  variant: PrintDocumentVariant = "preview",
  scriptFallbacks: readonly ScriptFallbackKey[] = [],
) {
  const surfaceBackground = sanitizeCssColor(previewSurfaceBackground);
  const pageSize = getPageSizeValue(options);
  const theme = getPdfThemeDefinition(options.theme);
  const mermaidConfig = buildMermaidInitializeConfig(
    getFontStack(options.diagramFontFamily, scriptFallbacks),
    options.diagramFontSize,
    options.theme,
    options.diagramTheme,
  );
  // Runs before the polyfill. Waits for fonts, renders deferred Mermaid in the
  // page context, then settles images so pagination metrics stay stable.
  // After preview(), variant-specific CSS is re-injected (Paged drops @media
  // screen rules). Shared handlers keep heading/pre/mermaid together.
  const afterBody =
    variant === "pdf"
      ? buildPdfAfterScript(pageSize, theme.isDark)
      : buildPreviewAfterScript();

  const beforeBody = buildBeforeBodyScript();
  const mermaidHelper = buildMermaidHelperScript(mermaidConfig);

  if (variant === "pdf") {
    return `
<script>
window.PagedConfig = {
  auto: true,
  before: async function () {
${beforeBody}
  },
  after: function (flow) {
${buildAfterCoreScript(afterBody)}
  }
};

${mermaidHelper}

// Keep the fallback path limited to the explicit Paged.js script load error
// above. Browsers report ResizeObserver loop notifications as window errors
// while Paged.js is laying out pages; treating those notifications as a
// completed fallback reveals partially paginated pages and makes the preview
// flicker before the pagination callback finishes.
</script>
`.trim();
  }

  // Preview: auto=false so the polyfill does not one-shot; we bootstrap on
  // script onload and warm-repaginate via postMessage without reloading Paged.
  return `
<script>
window.__markdown2pdfSurfaceBackground = ${JSON.stringify(surfaceBackground)};
window.__markdown2pdfMermaidConfig = ${JSON.stringify(mermaidConfig)};
window.__markdown2pdfHandlersRegistered = false;
window.__markdown2pdfPagedRunning = false;
window.__markdown2pdfPagedQueued = null;

window.PagedConfig = { auto: false };

async function __markdown2pdfBeforePaged() {
${beforeBody}
}

function __markdown2pdfAfterPaged(flow) {
${buildAfterCoreScript(afterBody)}
}

async function __markdown2pdfRunPaged() {
  if (!window.Paged || !window.Paged.Previewer) {
    document.documentElement.dataset.pagedStatus = "fallback";
    document.dispatchEvent(new CustomEvent("${pagedPreviewFallbackEvent}"));
    return;
  }
  var previewer = new window.Paged.Previewer();
  await __markdown2pdfBeforePaged();
  var flow = await previewer.preview();
  __markdown2pdfAfterPaged(flow);
}

window.__markdown2pdfBootstrapPaged = async function () {
  if (window.__markdown2pdfPagedBootstrapped) return;
  window.__markdown2pdfPagedBootstrapped = true;
  // Share the repaginate queue lock so a warm update cannot tear down the DOM
  // while the first preview() is still running.
  window.__markdown2pdfPagedQueued = window.__markdown2pdfPagedQueued || {
    revision: document.documentElement.dataset.previewRevision || "",
    bodyHtml: null,
    bootstrap: true,
  };
  __markdown2pdfPumpPagedQueue();
};

function __markdown2pdfResetPagedDom(bodyHtml) {
  var body = document.body;
  Array.prototype.slice.call(body.children).forEach(function (el) {
    if (el.tagName !== "SCRIPT") el.remove();
  });
  var pages = document.querySelector(".pagedjs_pages");
  if (pages) pages.remove();
  Array.prototype.slice
    .call(document.querySelectorAll("template[data-ref='pagedjs-content']"))
    .forEach(function (node) { node.remove(); });
  Array.prototype.slice
    .call(document.querySelectorAll("style[data-pagedjs-inserted-styles]"))
    .forEach(function (node) { node.remove(); });
  var chrome = document.getElementById("markdown2pdf-paged-chrome");
  if (chrome) chrome.remove();
  var main = document.createElement("main");
  main.className = "pdf-document markdown-preview";
  main.setAttribute("aria-label", "PDF document content");
  main.innerHTML = bodyHtml;
  body.insertBefore(main, body.firstChild);
}

function __markdown2pdfApplyPrintCss(printCss) {
  var existing = document.getElementById("markdown2pdf-print-css");
  if (existing) existing.remove();
  var style = document.createElement("style");
  style.id = "markdown2pdf-print-css";
  style.textContent = printCss;
  document.head.appendChild(style);
}

async function __markdown2pdfDoRepaginate(payload) {
  if (payload && payload.bootstrap) {
    await __markdown2pdfRunPaged();
    return;
  }
  document.documentElement.dataset.pagedStatus = "running";
  document.documentElement.dataset.previewRevision = String(payload.revision || "");
  document.documentElement.setAttribute("lang", payload.lang || "en");
  document.documentElement.setAttribute(
    "data-text-dir",
    payload.dir === "rtl" ? "rtl" : "ltr"
  );
  if (payload.previewSurfaceBackground) {
    window.__markdown2pdfSurfaceBackground = payload.previewSurfaceBackground;
  }
  if (payload.mermaidConfig) {
    window.__markdown2pdfMermaidConfig = payload.mermaidConfig;
  }
  __markdown2pdfResetPagedDom(payload.bodyHtml || "");
  if (typeof payload.printCss === "string") {
    __markdown2pdfApplyPrintCss(payload.printCss);
  }
  await __markdown2pdfRunPaged();
}

async function __markdown2pdfPumpPagedQueue() {
  if (window.__markdown2pdfPagedRunning) return;
  var next = window.__markdown2pdfPagedQueued;
  if (!next) return;
  // One payload per host dispatch. Do not drain a mid-run queue — that chained
  // full preview() passes and made typing during pagination extend forever.
  window.__markdown2pdfPagedQueued = null;
  window.__markdown2pdfPagedRunning = true;
  try {
    await __markdown2pdfDoRepaginate(next);
  } catch (e) {
    document.documentElement.dataset.pagedStatus = "fallback";
    document.dispatchEvent(new CustomEvent("${pagedPreviewFallbackEvent}"));
  } finally {
    window.__markdown2pdfPagedRunning = false;
    window.__markdown2pdfPagedQueued = null;
  }
}

window.__markdown2pdfRepaginate = function (payload) {
  // Host scheduler owns coalescing. Ignore updates while a run is in flight so
  // a stray postMessage cannot queue another preview() behind the current one.
  if (window.__markdown2pdfPagedRunning) return;
  window.__markdown2pdfPagedQueued = payload;
  __markdown2pdfPumpPagedQueue();
};

window.addEventListener("message", function (event) {
  // srcdoc frames have location.origin "null"; trust parent source instead.
  if (event.source !== window.parent) return;
  var data = event.data;
  if (!data || data.type !== ${JSON.stringify(pagedPreviewUpdateMessageType)}) return;
  window.__markdown2pdfRepaginate(data);
});

${mermaidHelper}

// Keep the fallback path limited to the explicit Paged.js script load error
// above. Browsers report ResizeObserver loop notifications as window errors
// while Paged.js is laying out pages; treating those notifications as a
// completed fallback reveals partially paginated pages and makes the preview
// flicker before the pagination callback finishes.
</script>
`.trim();
}

function buildAfterCoreScript(afterBody: string) {
  return `
    var pageCount = 0;
    if (flow && typeof flow.total === "number") {
      pageCount = flow.total;
    } else {
      pageCount = document.querySelectorAll(".pagedjs_page").length;
    }
    pageCount = Math.max(1, pageCount);

    // Apply document RTL only after layout. direction:rtl on .pagedjs_area
    // during measurement collapses multi-page Arabic docs to a single page.
    if (document.documentElement.getAttribute("data-text-dir") === "rtl") {
      Array.prototype.forEach.call(
        document.querySelectorAll(".pagedjs_page_content, .pagedjs_area"),
        function (el) {
          el.style.direction = "rtl";
        },
      );
    }

${afterBody}

    document.documentElement.dataset.pagedStatus = "complete";
    document.documentElement.dataset.pageCount = String(pageCount);
    document.dispatchEvent(new CustomEvent("${pagedPreviewCompleteEvent}", {
      detail: { pageCount: pageCount }
    }));
`.trim();
}

function buildBeforeBodyScript() {
  return `
    document.documentElement.dataset.pagedStatus = "running";

    function isKeepWithNextTarget(element) {
      if (!element || element.nodeType !== 1) return false;
      // Mermaid stays whole. Tables/details may split across pages (handlers
      // repair continuation fragments). Do not wrap <pre>: break-inside:avoid
      // on the wrapper truncates fences taller than one page (Paged.js clips
      // overflow). Heading page-break-after:avoid + AvoidBreakAfter still
      // prevent orphaned code headings.
      var tag = element.tagName;
      if (tag === "TABLE" || tag === "DETAILS") return false;
      return element.classList && element.classList.contains("mermaid-diagram");
    }

    // Wrap before preview() clones the source into the chunker.
    var root = document.querySelector("main.pdf-document");
    if (root) {
      var headings = root.querySelectorAll("h1, h2, h3, h4, h5, h6");
      Array.prototype.forEach.call(headings, function (heading) {
        var next = heading.nextElementSibling;
        if (!next || !isKeepWithNextTarget(next)) return;
        if (
          heading.parentElement &&
          heading.parentElement.classList &&
          heading.parentElement.classList.contains("md-keep-with-next")
        ) {
          return;
        }
        var wrap = document.createElement("div");
        wrap.className = "md-keep-with-next";
        heading.parentNode.insertBefore(wrap, heading);
        wrap.appendChild(heading);
        wrap.appendChild(next);
      });

      // Firefox + Paged.js: CSS multi-column cannot fragment real tables
      // (pagedjs#175/#254). Force rows into block/grid flow so tall tables
      // split instead of blanking a page and parking the heading alone.
      var isGecko =
        /Firefox\\//.test(navigator.userAgent) &&
        !/FxiOS\\//.test(navigator.userAgent);
      if (isGecko) {
        Array.prototype.forEach.call(root.querySelectorAll("table"), function (table) {
          var sampleRow = table.querySelector("tr");
          if (!sampleRow) return;
          var cols = sampleRow.children.length || 1;
          table.classList.add("md-table-flat");
          table.style.setProperty("--md-table-cols", String(cols));
          Array.prototype.forEach.call(table.querySelectorAll("tr"), function (tr) {
            var spanTotal = 0;
            Array.prototype.forEach.call(tr.children, function (cell) {
              var span = parseInt(cell.getAttribute("colspan") || "1", 10);
              if (!(span > 0)) span = 1;
              spanTotal += span;
              if (span > 1) {
                cell.style.gridColumn = "span " + span;
              }
            });
            var rowCols = spanTotal > 0 ? spanTotal : cols;
            tr.style.gridTemplateColumns =
              "repeat(" + rowCols + ", minmax(0, 1fr))";
          });
        });
      }
    }

    // Register before preview() so initializeHandlers() picks it up.
    if (
      !window.__markdown2pdfHandlersRegistered &&
      window.Paged &&
      window.Paged.Handler &&
      window.Paged.registerHandlers
    ) {
      window.__markdown2pdfHandlersRegistered = true;
      function isBreakInsideAvoid(element) {
        if (!element || element.nodeType !== 1) return false;
        var style = window.getComputedStyle(element);
        return style.breakInside === "avoid" || style.pageBreakInside === "avoid";
      }

      function overflowStartElement(overflow) {
        var node = overflow.startContainer;
        if (node && node.nodeType === 3) node = node.parentElement;
        if (node && node.nodeType === 1 && overflow.startOffset > 0) {
          var child = node.childNodes[overflow.startOffset];
          if (child) {
            if (child.nodeType === 3) child = child.parentElement;
            if (child && child.nodeType === 1) node = child;
          }
        }
        return node && node.nodeType === 1 ? node : null;
      }

      function outermostBreakInsideAvoid(element, rootEl) {
        var avoid = null;
        var el = element;
        while (el && el !== rootEl) {
          if (isBreakInsideAvoid(el)) avoid = el;
          el = el.parentElement;
        }
        return avoid;
      }

      // Rescue in-column false rejects; never trust overflow-column rects.
      class FitAvoidOverflow extends window.Paged.Handler {
        constructor(chunker, polisher, caller) {
          super(chunker, polisher, caller);
        }
        onOverflow(overflow, rendered, bounds) {
          if (!overflow || !rendered || !bounds) return;

          var start = overflowStartElement(overflow);
          if (!start) return;

          var avoid = null;
          if (
            start.getAttribute("data-break-after") === "avoid" &&
            start.nextElementSibling &&
            isBreakInsideAvoid(start.nextElementSibling)
          ) {
            avoid = start.nextElementSibling;
          } else {
            avoid = outermostBreakInsideAvoid(start, rendered);
          }
          if (!avoid) return;

          var groupTop = avoid;
          if (
            avoid.previousElementSibling &&
            avoid.previousElementSibling.getAttribute("data-break-after") === "avoid"
          ) {
            groupTop = avoid.previousElementSibling;
          }

          var avoidRect = avoid.getBoundingClientRect();
          var groupRect = groupTop.getBoundingClientRect();
          // Overflow column: tops reset to the page top — bottom checks lie.
          var avoidInOverflowColumn = avoidRect.left >= bounds.right - 1;
          var groupInOverflowColumn = groupRect.left >= bounds.right - 1;

          if (avoidInOverflowColumn) {
            if (groupInOverflowColumn) return;
            var remaining = bounds.bottom - groupRect.bottom;
            if (avoid.offsetHeight > remaining + 1) return;
          } else if (
            Math.max(groupRect.bottom, avoidRect.bottom) >
            bounds.bottom + 0.5
          ) {
            return;
          }

          var after = avoid.nextElementSibling;
          if (!after) return null;

          var range = document.createRange();
          range.setStartBefore(after);
          range.setEndAfter(rendered.lastChild);
          return range;
        }
      }

      class AvoidBreakAfter extends window.Paged.Handler {
        constructor(chunker, polisher, caller) {
          super(chunker, polisher, caller);
        }
        afterPageLayout(pageElement, page, breakToken) {
          if (!breakToken || !breakToken.node || !page || !page.area) return;

          var orphans = Array.prototype.slice
            .call(page.area.querySelectorAll("[data-break-after='avoid']"))
            .filter(function (element) {
              return !element.nextElementSibling;
            });
          if (!orphans.length) return;

          // Last in tree order is the bottom-of-page orphan.
          var moveStart = orphans[orphans.length - 1];
          while (
            moveStart.previousElementSibling &&
            moveStart.previousElementSibling.getAttribute("data-break-after") === "avoid"
          ) {
            moveStart = moveStart.previousElementSibling;
          }

          var ref = moveStart.getAttribute("data-ref");
          if (!ref || !this.chunker || !this.chunker.source) return;
          var sourceNode = this.chunker.source.querySelector(
            '[data-ref="' + ref + '"]'
          );
          if (!sourceNode) return;

          breakToken.node = sourceNode;
          breakToken.offset = 0;

          var node = moveStart;
          while (node) {
            var next = node.nextElementSibling;
            node.remove();
            node = next;
          }
        }
      }

      // Paged.js can leave the first overflowing .md-code-line in the
      // measurement overflow column while advancing the break token past it.
      // That line stays in the page 1 DOM (textContent finds it) but is painted
      // off-sheet and clipped by overflow:hidden — preview/PDF both drop it.
      class RescueOverflowCodeLines extends window.Paged.Handler {
        constructor(chunker, polisher, caller) {
          super(chunker, polisher, caller);
        }
        afterPageLayout(pageElement, page, breakToken) {
          if (!breakToken || !breakToken.node || !page || !page.area) return;

          var bounds = page.area.getBoundingClientRect();
          var stranded = Array.prototype.slice
            .call(page.area.querySelectorAll(".md-code-line"))
            .filter(function (element) {
              var rect = element.getBoundingClientRect();
              return (
                rect.left >= bounds.right - 1 ||
                rect.top >= bounds.bottom - 0.5
              );
            });
          if (!stranded.length) return;

          var moveStart = stranded[0];
          var codeParent = moveStart.parentElement;
          var pre =
            moveStart.closest && moveStart.closest("pre")
              ? moveStart.closest("pre")
              : null;

          // If this page still has room for ≥5 code lines, the stranding is
          // likely a false overflow-column hit — skip rescue. Only rewind when
          // the page is effectively full (less than 5 lines of free space).
          var lineHeight = moveStart.getBoundingClientRect().height;
          if (!(lineHeight > 0) && codeParent) {
            var sample = codeParent.querySelector(":scope > .md-code-line");
            if (sample) lineHeight = sample.getBoundingClientRect().height;
          }
          if (!(lineHeight > 0)) {
            try {
              lineHeight = parseFloat(window.getComputedStyle(moveStart).lineHeight) || 0;
            } catch (e) {
              lineHeight = 0;
            }
          }
          if (lineHeight > 0) {
            var keptBottoms = Array.prototype.slice
              .call(
                codeParent && codeParent.tagName === "CODE"
                  ? codeParent.querySelectorAll(":scope > .md-code-line")
                  : [],
              )
              .filter(function (line) {
                return stranded.indexOf(line) === -1;
              })
              .map(function (line) {
                return line.getBoundingClientRect().bottom;
              });
            // No on-sheet lines ⇒ page is empty of this fence; always rescue.
            // Otherwise skip when ≥5 line-heights of free space remain.
            if (keptBottoms.length > 0) {
              var contentBottom = Math.max.apply(Math, keptBottoms);
              var remaining = bounds.bottom - contentBottom;
              if (remaining >= 5 * lineHeight) return;
            }
          }

          // If every line of this fence is leaving the page, move the whole
          // <pre> — otherwise we leave an empty bordered shell (top radius
          // only) at the bottom of the page.
          var removeWholePre = false;
          if (pre && codeParent && codeParent.tagName === "CODE") {
            var firstLine = codeParent.querySelector(":scope > .md-code-line");
            removeWholePre = Boolean(firstLine === moveStart);
          }

          var tokenEl = removeWholePre && pre ? pre : moveStart;
          var ref = tokenEl.getAttribute("data-ref");
          if (!ref || !this.chunker || !this.chunker.source) return;
          var sourceNode = this.chunker.source.querySelector(
            '[data-ref="' + ref + '"]'
          );
          if (!sourceNode) return;

          breakToken.node = sourceNode;
          breakToken.offset = 0;

          if (removeWholePre && pre) {
            pre.remove();
            return;
          }

          var node = moveStart;
          while (node) {
            var next = node.nextElementSibling;
            node.remove();
            node = next;
          }
        }
      }

      // Drop hollow split-to fences (border/padding only). Happens when a
      // break pushes every line to the next page but leaves the <pre> shell.
      class RescueEmptySplitPre extends window.Paged.Handler {
        constructor(chunker, polisher, caller) {
          super(chunker, polisher, caller);
        }
        afterPageLayout(pageElement, page, breakToken) {
          if (!breakToken || !breakToken.node || !page || !page.area) return;

          var shells = Array.prototype.slice
            .call(page.area.querySelectorAll("pre[data-split-to]"))
            .filter(function (pre) {
              var lines = Array.prototype.slice.call(
                pre.querySelectorAll(":scope > code > .md-code-line"),
              );
              if (!lines.length) return true;
              return lines.every(function (line) {
                var text = (line.textContent || "")
                  .replace(/\u00a0/g, " ")
                  .trim();
                return text.length === 0;
              });
            });
          if (!shells.length) return;

          var pre = shells[shells.length - 1];
          var ref = pre.getAttribute("data-ref");
          if (!ref || !this.chunker || !this.chunker.source) return;
          var sourceNode = this.chunker.source.querySelector(
            '[data-ref="' + ref + '"]'
          );
          if (!sourceNode) return;

          breakToken.node = sourceNode;
          breakToken.offset = 0;
          pre.remove();
        }
      }

      // Orphan split: fewer than 4 on-sheet lines at the bottom of a page
      // before a fence continues — pull the whole <pre> to the next page.
      // Fragments with ≥4 lines are left alone (healthy splits).
      // Note: data-split-to is often applied AFTER afterPageLayout, so detect
      // continuation via breakToken still pointing inside the source <pre>.
      class RescueOrphanSplitPre extends window.Paged.Handler {
        constructor(chunker, polisher, caller) {
          super(chunker, polisher, caller);
        }
        afterPageLayout(pageElement, page, breakToken) {
          if (!breakToken || !breakToken.node || !page || !page.area) return;
          if (!this.chunker || !this.chunker.source) return;

          var tokenNode = breakToken.node;
          if (tokenNode.nodeType === 3) tokenNode = tokenNode.parentElement;
          if (!tokenNode || tokenNode.nodeType !== 1) return;

          var bounds = page.area.getBoundingClientRect();

          function isInside(node, ancestor) {
            var el = node;
            while (el && el.nodeType === 1) {
              if (el === ancestor) return true;
              el = el.parentElement;
            }
            return false;
          }

          function visibleLineCount(pre) {
            return Array.prototype.slice
              .call(pre.querySelectorAll(".md-code-line"))
              .filter(function (line) {
                var text = (line.textContent || "")
                  .replace(/\u00a0/g, " ")
                  .trim();
                if (!text.length) return false;
                var rect = line.getBoundingClientRect();
                return !(
                  rect.left >= bounds.right - 1 ||
                  rect.top >= bounds.bottom - 0.5
                );
              }).length;
          }

          var orphans = Array.prototype.slice
            .call(page.area.querySelectorAll("pre"))
            .filter(function (pre) {
              var count = visibleLineCount(pre);
              if (!(count > 0 && count < 4)) return false;
              var ref = pre.getAttribute("data-ref");
              if (!ref) return false;
              var sourcePre = this.chunker.source.querySelector(
                '[data-ref="' + ref + '"]',
              );
              if (!sourcePre) return false;
              // Only when the break still falls inside this fence (it continues).
              return isInside(tokenNode, sourcePre);
            }, this);
          if (!orphans.length) return;

          var pre = orphans[orphans.length - 1];
          var ref = pre.getAttribute("data-ref");
          var sourceNode = this.chunker.source.querySelector(
            '[data-ref="' + ref + '"]',
          );
          if (!sourceNode) return;

          breakToken.node = sourceNode;
          breakToken.offset = 0;
          pre.remove();
        }
      }

      // Hollow / orphan details split: fewer than 4 on-sheet body lines before
      // the block continues — pull the whole <details> to the next page.
      // Fragments with ≥4 body lines are left alone (continuation summary
      // is injected by FixSplitDetailsContinuation).
      class RescueOrphanSplitDetails extends window.Paged.Handler {
        constructor(chunker, polisher, caller) {
          super(chunker, polisher, caller);
        }
        afterPageLayout(pageElement, page, breakToken) {
          if (!breakToken || !breakToken.node || !page || !page.area) return;
          if (!this.chunker || !this.chunker.source) return;

          var tokenNode = breakToken.node;
          if (tokenNode.nodeType === 3) tokenNode = tokenNode.parentElement;
          if (!tokenNode || tokenNode.nodeType !== 1) return;

          var bounds = page.area.getBoundingClientRect();

          function isInside(node, ancestor) {
            var el = node;
            while (el && el.nodeType === 1) {
              if (el === ancestor) return true;
              el = el.parentElement;
            }
            return false;
          }

          function isVisibleOnPage(el) {
            var rect = el.getBoundingClientRect();
            return !(
              rect.left >= bounds.right - 1 ||
              rect.top >= bounds.bottom - 0.5 ||
              rect.height < 0.5
            );
          }

          // Approximate visible body lines (exclude <summary>), same <4 threshold
          // as code fences.
          function visibleBodyLineCount(details) {
            var total = 0;
            Array.prototype.forEach.call(details.children, function (child) {
              if (child.nodeType !== 1 || child.tagName === "SUMMARY") return;
              if (!isVisibleOnPage(child)) return;
              var rect = child.getBoundingClientRect();
              var lineHeight = 0;
              try {
                lineHeight = parseFloat(window.getComputedStyle(child).lineHeight) || 0;
              } catch (e) {
                lineHeight = 0;
              }
              if (!(lineHeight > 0)) lineHeight = 16;
              total += Math.max(1, Math.round(rect.height / lineHeight));
            });
            return total;
          }

          var orphans = Array.prototype.slice
            .call(page.area.querySelectorAll("details"))
            .filter(function (details) {
              var count = visibleBodyLineCount(details);
              if (!(count < 4)) return false;
              var ref = details.getAttribute("data-ref");
              if (!ref) return false;
              var sourceDetails = this.chunker.source.querySelector(
                '[data-ref="' + ref + '"]',
              );
              if (!sourceDetails) return false;
              return isInside(tokenNode, sourceDetails);
            }, this);
          if (!orphans.length) return;

          var details = orphans[orphans.length - 1];
          var ref = details.getAttribute("data-ref");
          var sourceNode = this.chunker.source.querySelector(
            '[data-ref="' + ref + '"]',
          );
          if (!sourceNode) return;

          breakToken.node = sourceNode;
          breakToken.offset = 0;
          details.remove();
        }
      }

      // Orphan table split: fewer than 4 visible rows before the table
      // continues — pull the whole <table> to the next page (same <4 rule).
      class RescueOrphanSplitTable extends window.Paged.Handler {
        constructor(chunker, polisher, caller) {
          super(chunker, polisher, caller);
        }
        afterPageLayout(pageElement, page, breakToken) {
          if (!breakToken || !breakToken.node || !page || !page.area) return;
          if (!this.chunker || !this.chunker.source) return;

          var tokenNode = breakToken.node;
          if (tokenNode.nodeType === 3) tokenNode = tokenNode.parentElement;
          if (!tokenNode || tokenNode.nodeType !== 1) return;

          var bounds = page.area.getBoundingClientRect();

          function isInside(node, ancestor) {
            var el = node;
            while (el && el.nodeType === 1) {
              if (el === ancestor) return true;
              el = el.parentElement;
            }
            return false;
          }

          function visibleRowCount(table) {
            return Array.prototype.slice
              .call(table.querySelectorAll("tr"))
              .filter(function (row) {
                var text = (row.textContent || "").replace(/\u00a0/g, " ").trim();
                if (!text.length) return false;
                var rect = row.getBoundingClientRect();
                return !(
                  rect.left >= bounds.right - 1 ||
                  rect.top >= bounds.bottom - 0.5 ||
                  rect.height < 0.5
                );
              }).length;
          }

          var orphans = Array.prototype.slice
            .call(page.area.querySelectorAll("table"))
            .filter(function (table) {
              var count = visibleRowCount(table);
              if (!(count < 4)) return false;
              var ref = table.getAttribute("data-ref");
              if (!ref) return false;
              var sourceTable = this.chunker.source.querySelector(
                '[data-ref="' + ref + '"]',
              );
              if (!sourceTable) return false;
              return isInside(tokenNode, sourceTable);
            }, this);
          if (!orphans.length) return;

          var table = orphans[orphans.length - 1];
          var ref = table.getAttribute("data-ref");
          var sourceNode = this.chunker.source.querySelector(
            '[data-ref="' + ref + '"]',
          );
          if (!sourceNode) return;

          breakToken.node = sourceNode;
          breakToken.offset = 0;
          table.remove();
        }
      }

      // Continuation pages get a summary so the browser does not invent "Details".
      class FixSplitDetailsContinuation extends window.Paged.Handler {
        constructor(chunker, polisher, caller) {
          super(chunker, polisher, caller);
          this.splitDetailsRefs = [];
        }
        afterPageLayout(pageElement, page, breakToken) {
          this.splitDetailsRefs = [];
          if (!breakToken || !breakToken.node) return;

          var node = breakToken.node;
          if (node.nodeType === 3) node = node.parentElement;
          if (!node || node.nodeType !== 1) return;

          var refs = [];
          var el = node;
          while (el && el.nodeType === 1) {
            if (el.tagName === "DETAILS") {
              var ref = el.getAttribute("data-ref");
              if (ref) refs.push(ref);
            }
            el = el.parentElement;
          }
          this.splitDetailsRefs = refs;
        }
        layout(rendered) {
          if (!this.splitDetailsRefs.length) return;
          if (!this.chunker || !this.chunker.source) return;

          var source = this.chunker.source;
          this.splitDetailsRefs.forEach(function (ref) {
            var renderedDetails = rendered.querySelector(
              'details[data-ref="' + ref + '"]',
            );
            if (!renderedDetails) return;
            if (renderedDetails.querySelector(":scope > summary")) return;
            if (renderedDetails.getAttribute("data-continued-summary")) return;

            var sourceDetails = source.querySelector(
              'details[data-ref="' + ref + '"]',
            );
            if (!sourceDetails) return;

            var sourceSummary = sourceDetails.querySelector(":scope > summary");
            var label = sourceSummary
              ? (sourceSummary.textContent || "").trim()
              : "";
            if (!label) label = "Details";

            var summary = document.createElement("summary");
            summary.textContent = label + " (cont.)";
            renderedDetails.insertBefore(summary, renderedDetails.firstChild);
            renderedDetails.setAttribute("data-continued-summary", "true");
            renderedDetails.setAttribute("open", "");
          });
        }
      }

      // Repeat table headers on continuation pages (Paged.js 0.4.x).
      // Prefer layout() so thead height is included in overflow; afterPageLayout
      // / afterRendered are fallbacks when attributes land late.
      class RepeatTableHeaders extends window.Paged.Handler {
        constructor(chunker, polisher, caller) {
          super(chunker, polisher, caller);
        }
        ensureRepeatedHeaders(root) {
          if (!root || !this.chunker || !this.chunker.source) return;
          var source = this.chunker.source;
          Array.prototype.forEach.call(
            root.querySelectorAll("table"),
            function (table) {
              if (table.getAttribute("data-repeated-headers")) return;

              var ref = table.getAttribute("data-ref");
              if (!ref) return;

              var sourceTable = source.querySelector(
                'table[data-ref="' + ref + '"]',
              );
              if (!sourceTable) return;

              var sourceThead = sourceTable.querySelector(":scope > thead");
              if (!sourceThead) return;

              // Only continuations (rebuildAncestors sets data-split-from).
              if (!table.hasAttribute("data-split-from")) return;
              if (table.querySelector(":scope > thead")) {
                table.setAttribute("data-repeated-headers", "true");
                return;
              }

              var first = table.firstChild;
              Array.prototype.forEach.call(
                sourceTable.querySelectorAll(":scope > colgroup"),
                function (colgroup) {
                  table.insertBefore(colgroup.cloneNode(true), first);
                },
              );
              table.insertBefore(sourceThead.cloneNode(true), table.firstChild);
              table.setAttribute("data-repeated-headers", "true");
            },
          );
        }
        layout(rendered) {
          this.ensureRepeatedHeaders(rendered);
        }
        afterPageLayout(pageElement, page, breakToken) {
          this.ensureRepeatedHeaders(pageElement);

          // Do not leave the break token inside <thead> — advance past it.
          if (!breakToken || !breakToken.node) return;
          var node = breakToken.node;
          if (node.nodeType === 3) node = node.parentElement;
          if (!node || node.nodeType !== 1) return;

          var thead =
            node.tagName === "THEAD"
              ? node
              : node.closest
                ? node.closest("thead")
                : null;
          if (thead && this.chunker && this.chunker.source) {
            var theadRef = thead.getAttribute("data-ref");
            var sourceThead = theadRef
              ? this.chunker.source.querySelector(
                  '[data-ref="' + theadRef + '"]',
                )
              : null;
            if (sourceThead && sourceThead.nextElementSibling) {
              breakToken.node = sourceThead.nextElementSibling;
              breakToken.offset = 0;
            }
          }
        }
        afterRendered(pages) {
          if (!pages || !pages.length) return;
          var self = this;
          pages.forEach(function (page) {
            self.ensureRepeatedHeaders(page.element || page.area);
          });
        }
      }

      window.Paged.registerHandlers(
        FitAvoidOverflow,
        AvoidBreakAfter,
        RescueOverflowCodeLines,
        RescueEmptySplitPre,
        RescueOrphanSplitPre,
        RescueOrphanSplitDetails,
        RescueOrphanSplitTable,
        FixSplitDetailsContinuation,
        RepeatTableHeaders,
      );
    }

    if (document.fonts && document.fonts.ready) {
      try { await document.fonts.ready; } catch (e) {}
    }
    await window.__markdown2pdfRenderPendingMermaid();
    var images = Array.prototype.slice.call(document.images || []);
    await Promise.all(images.map(function (img) {
      if (img.complete) return Promise.resolve();
      return new Promise(function (resolve) {
        img.addEventListener("load", resolve, { once: true });
        img.addEventListener("error", resolve, { once: true });
      });
    }));
`.trim();
}

function buildMermaidHelperScript(
  mermaidConfig: ReturnType<typeof buildMermaidInitializeConfig>,
) {
  return `
window.__markdown2pdfMermaidConfig = window.__markdown2pdfMermaidConfig || ${JSON.stringify(mermaidConfig)};

window.__markdown2pdfRenderPendingMermaid = async function () {
  var pending = Array.prototype.slice.call(
    document.querySelectorAll('.mermaid-diagram[data-mermaid-status="pending"]')
  );
  if (!pending.length) return;

  function markError(diagram) {
    var message = document.createElement("p");
    message.textContent = "Mermaid diagram could not render.";
    diagram.replaceChildren(message);
    diagram.classList.add("mermaid-diagram-fallback");
    diagram.setAttribute("data-mermaid-status", "error");
  }

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      var script = document.createElement("script");
      script.src = src;
      script.onload = function () { resolve(); };
      script.onerror = function () { reject(new Error("Failed to load " + src)); };
      document.head.appendChild(script);
    });
  }

  try {
    if (!window.mermaid) {
      await loadScript(${JSON.stringify(mermaidScriptHref)});
    }
  } catch (e) {
    pending.forEach(markError);
    return;
  }

  if (!window.mermaid) {
    pending.forEach(markError);
    return;
  }

  window.mermaid.initialize(window.__markdown2pdfMermaidConfig);

  for (var index = 0; index < pending.length; index += 1) {
    var diagram = pending[index];
    var code = diagram.querySelector("code");
    var source = code && code.textContent ? code.textContent : "";

    try {
      var parsed = await window.mermaid.parse(source, { suppressErrors: true });
      if (!parsed) throw new Error("Invalid Mermaid diagram.");
      var rendered = await window.mermaid.render(
        "markdown2pdf-preview-mermaid-" + index + "-" + Date.now(),
        source
      );
      var svg = rendered && rendered.svg ? rendered.svg : "";
      if (/<script\\b|<image\\b|\\b(?:href|xlink:href)=["']\\s*(?:javascript:|data:)|url\\(\\s*["']?\\s*(?:https?:|data:|javascript:)/i.test(svg)) {
        throw new Error("Unsafe Mermaid SVG output.");
      }
      diagram.innerHTML = svg;
      diagram.setAttribute("data-mermaid-status", "rendered");
    } catch (e) {
      markError(diagram);
    }
  }
};
`.trim();
}

/**
 * Install in-document hash click scrolling for the Paged.js preview.
 * Native hash jumps hit hidden/pre-chunk source nodes and blank overflow:hidden
 * sheets; Firefox/Zen srcdoc + allow-same-origin also resolves #id against the
 * parent origin and navigates the iframe into a frame-ancestors-blocked page.
 */
export function buildPreviewHashNavigationInstallSource() {
  return `
    if (!document.documentElement.dataset.hashNavBound) {
      document.documentElement.dataset.hashNavBound = "1";
      document.addEventListener("click", function (event) {
        var anchor = event.target && event.target.closest
          ? event.target.closest('a[href^="#"]')
          : null;
        if (!anchor) return;
        var href = anchor.getAttribute("href");
        if (!href || href === "#") return;
        var id = href.slice(1);
        if (!id) return;
        event.preventDefault();
        event.stopPropagation();
        var target = null;
        var pages = document.querySelector(".pagedjs_pages");
        if (pages) {
          var candidates = pages.querySelectorAll("[id]");
          for (var i = 0; i < candidates.length; i++) {
            if (candidates[i].id === id) {
              target = candidates[i];
              break;
            }
          }
        }
        if (!target) target = document.getElementById(id);
        if (target && typeof target.scrollIntoView === "function") {
          // Match PDF jumps: put the heading at the top of the viewport.
          target.scrollIntoView({ block: "start" });
        }
      }, true);
    }
`.trim();
}

const previewHashNavScriptMarker = 'data-preview-hash-nav="1"';

/**
 * Prerendered sample snapshots strip scripts but leave data-hash-nav-bound set.
 * Re-inject the click interceptor and clear the stale bound flag.
 */
export function withSamplePreviewHashNavigation(html: string) {
  const withoutStaleBound = html.replace(/\s*data-hash-nav-bound="1"/g, "");
  if (withoutStaleBound.includes(previewHashNavScriptMarker)) {
    return withoutStaleBound;
  }

  const script = `<script ${previewHashNavScriptMarker}>\n${buildPreviewHashNavigationInstallSource()}\n</script>`;
  if (/<\/body>/i.test(withoutStaleBound)) {
    return withoutStaleBound.replace(/<\/body>/i, `${script}</body>`);
  }
  if (/<\/html>/i.test(withoutStaleBound)) {
    return withoutStaleBound.replace(/<\/html>/i, `${script}</html>`);
  }
  return `${withoutStaleBound}${script}`;
}

function buildPreviewAfterScript() {
  return `
    var existingChrome = document.getElementById("markdown2pdf-paged-chrome");
    if (existingChrome) existingChrome.remove();
    var chrome = document.createElement("style");
    chrome.id = "markdown2pdf-paged-chrome";
    var surface = window.__markdown2pdfSurfaceBackground || ${JSON.stringify(defaultSurfaceFallback())};
    // Re-assert after Paged.js rewrites stylesheets (it drops @media screen
    // rules from the print-document CSS, including overflow/scrollbar hides).
    chrome.textContent = [
      "html, body {",
      "  background: " + surface + " !important;",
      "  overflow: hidden !important;",
      "  scrollbar-width: none !important;",
      "}",
      "html::-webkit-scrollbar, body::-webkit-scrollbar {",
      "  width: 0 !important;",
      "  height: 0 !important;",
      "  display: none !important;",
      "}",
      ".pagedjs_pages {",
      "  background: " + surface + " !important;",
      "  display: flex !important;",
      "  flex-direction: column !important;",
      "  gap: 1.5rem !important;",
      "  margin: 0 auto !important;",
      "  padding: 0.25rem 0 0.5rem !important;",
      "  width: fit-content !important;",
      "}",
      ".pagedjs_page {",
      "  background: var(--md-page, #ffffff) !important;",
      "  border: 1px solid var(--md-sheet-border, #d4d4d8) !important;",
      "  border-radius: 0.5rem !important;",
      "  box-shadow: var(--md-sheet-shadow, 0 1px 2px rgba(0,0,0,.04)) !important;",
      "  color: var(--md-fg, #24292f) !important;",
      "  flex: 0 0 auto !important;",
      "  margin: 0 !important;",
      "  overflow: hidden !important;",
      "}",
      ".pagedjs_sheet,",
      ".pagedjs_page_content,",
      ".pagedjs_area {",
      "  background: var(--md-page, #ffffff) !important;",
      "  color: var(--md-fg, #24292f) !important;",
      "}",
      ".pagedjs_sheet { border-radius: inherit; overflow: hidden; }",
      ".pagedjs_margin-bottom-center, .pagedjs_margin-top-center {",
      "  align-items: center !important;",
      "  display: flex !important;",
      "  justify-content: center !important;",
      "  text-align: center !important;",
      "  width: 100% !important;",
      "}",
    ].join("\\n");
    document.head.appendChild(chrome);

${buildPreviewHashNavigationInstallSource()}
`.trim();
}

function defaultSurfaceFallback() {
  return "#eef2f6";
}

function buildPdfAfterScript(pageSize: string, snapPageSizeForViewer: boolean) {
  return `
    var existingPrint = document.getElementById("markdown2pdf-paged-print");
    if (existingPrint) existingPrint.remove();
    var printStyle = document.createElement("style");
    printStyle.id = "markdown2pdf-paged-print";
    var pageSizeValue = ${JSON.stringify(pageSize)};
    // Resolve theme colors first. Firefox print often drops CSS-variable
    // backgrounds (including @page), which leaves the default white media box
    // as a 1px edge hairline on dark themes.
    var pageColor = (
      getComputedStyle(document.documentElement).getPropertyValue("--md-page") ||
      "#ffffff"
    ).trim();
    var fgColor = (
      getComputedStyle(document.documentElement).getPropertyValue("--md-fg") ||
      "#24292f"
    ).trim();
    if (!/^#(?:[0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(pageColor) &&
        !/^rgba?\\(\\s*[\\d.]+\\s*(?:,\\s*[\\d.]+\\s*){2,3}\\)$/i.test(pageColor)) {
      pageColor = "#ffffff";
    }
    if (!/^#(?:[0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(fgColor) &&
        !/^rgba?\\(\\s*[\\d.]+\\s*(?:,\\s*[\\d.]+\\s*){2,3}\\)$/i.test(fgColor)) {
      fgColor = "#24292f";
    }
    // Dark themes only: Firefox/cairo stores integer-pt MediaBoxes. PDF.js at
    // 100% zoom paints canvas CSS px = pts * 96/72 (= pts * 4/3). Non-multiples
    // of 3pt yield a fractional canvas and a white hairline. Snap @page down so
    // the sheet slightly overflows and clips cleanly at integer CSS pixels.
    if (${snapPageSizeForViewer ? "true" : "false"}) {
      var sheet = document.querySelector(".pagedjs_page");
      if (sheet instanceof HTMLElement && sheet.offsetWidth > 0 && sheet.offsetHeight > 0) {
        var ptW = Math.floor((sheet.offsetWidth * 72) / 96 / 3) * 3;
        var ptH = Math.floor((sheet.offsetHeight * 72) / 96 / 3) * 3;
        if (ptW > 0 && ptH > 0) {
          pageSizeValue = ptW + "pt " + ptH + "pt";
        }
      }
    }
    // Sheets are already paginated; each sheet must fill the PDF media box.
    // Bake concrete colors into @page + every layer (vars are unreliable in
    // Firefox print). Do not paint outside the page box (outline / exterior
    // box-shadow / negative inset) — Gecko and Chromium both treat that
    // overflow as an extra trailing blank sheet.
    printStyle.textContent = [
      "@page {",
      "  size: " + pageSizeValue + ";",
      "  margin: 0;",
      "  background: " + pageColor + ";",
      "  background-color: " + pageColor + ";",
      "}",
      "html, body {",
      "  background: " + pageColor + " !important;",
      "  background-color: " + pageColor + " !important;",
      "  color: " + fgColor + " !important;",
      "  margin: 0 !important;",
      "  padding: 0 !important;",
      "  -webkit-print-color-adjust: exact !important;",
      "  print-color-adjust: exact !important;",
      "  color-adjust: exact !important;",
      "}",
      ".pagedjs_pages {",
      "  background: " + pageColor + " !important;",
      "  background-color: " + pageColor + " !important;",
      "  display: block !important;",
      "  gap: 0 !important;",
      "  margin: 0 !important;",
      "  padding: 0 !important;",
      "  width: auto !important;",
      "}",
      ".pagedjs_page {",
      "  position: relative !important;",
      "  background: " + pageColor + " !important;",
      "  background-color: " + pageColor + " !important;",
      "  border: none !important;",
      "  border-radius: 0 !important;",
      "  box-shadow: none !important;",
      "  outline: none !important;",
      "  color: " + fgColor + " !important;",
      "  break-after: page !important;",
      "  margin: 0 !important;",
      "  overflow: hidden !important;",
      "  page-break-after: always !important;",
      "  -webkit-print-color-adjust: exact !important;",
      "  print-color-adjust: exact !important;",
      "  color-adjust: exact !important;",
      "}",
      ".pagedjs_page:last-child,",
      ".pagedjs_page:last-of-type {",
      "  break-after: auto !important;",
      "  page-break-after: auto !important;",
      "}",
      ".pagedjs_sheet,",
      ".pagedjs_pagebox,",
      ".pagedjs_page_content,",
      ".pagedjs_area,",
      ".pagedjs_margin,",
      ".pagedjs_margin-top,",
      ".pagedjs_margin-right,",
      ".pagedjs_margin-bottom,",
      ".pagedjs_margin-left,",
      ".pagedjs_margin-top-left-corner-holder,",
      ".pagedjs_margin-top-right-corner-holder,",
      ".pagedjs_margin-bottom-left-corner-holder,",
      ".pagedjs_margin-bottom-right-corner-holder {",
      "  background: " + pageColor + " !important;",
      "  background-color: " + pageColor + " !important;",
      "  color: " + fgColor + " !important;",
      "  -webkit-print-color-adjust: exact !important;",
      "  print-color-adjust: exact !important;",
      "  color-adjust: exact !important;",
      "}",
      ".pagedjs_sheet { position: relative !important; z-index: 1 !important; }",
      ".pagedjs_margin-bottom-center, .pagedjs_margin-top-center {",
      "  align-items: center !important;",
      "  display: flex !important;",
      "  justify-content: center !important;",
      "  text-align: center !important;",
      "  width: 100% !important;",
      "}",
      // Hide pre-chunk source / non-sheet nodes; only .pagedjs_pages prints.
      "body > *:not(.pagedjs_pages) { display: none !important; }",
    ].join("\\n");
    document.head.appendChild(printStyle);

    // Also stamp inline styles — Firefox print can ignore stylesheet paint
    // for some Paged.js layers even when @page uses a concrete color.
    document.documentElement.style.setProperty("background", pageColor, "important");
    document.documentElement.style.setProperty("background-color", pageColor, "important");
    document.documentElement.style.setProperty("color", fgColor, "important");
    document.body.style.setProperty("background", pageColor, "important");
    document.body.style.setProperty("background-color", pageColor, "important");
    document.body.style.setProperty("color", fgColor, "important");
    var paintTargets = document.querySelectorAll(
      ".pagedjs_pages, .pagedjs_page, .pagedjs_sheet, .pagedjs_pagebox, .pagedjs_page_content, .pagedjs_area, .pagedjs_margin, .pagedjs_margin-top, .pagedjs_margin-right, .pagedjs_margin-bottom, .pagedjs_margin-left, .pagedjs_margin-top-left-corner-holder, .pagedjs_margin-top-right-corner-holder, .pagedjs_margin-bottom-left-corner-holder, .pagedjs_margin-bottom-right-corner-holder"
    );
    for (var i = 0; i < paintTargets.length; i++) {
      var el = paintTargets[i];
      el.style.setProperty("background", pageColor, "important");
      el.style.setProperty("background-color", pageColor, "important");
      el.style.setProperty("color", fgColor, "important");
      el.style.setProperty("-webkit-print-color-adjust", "exact", "important");
      el.style.setProperty("print-color-adjust", "exact", "important");
      el.style.setProperty("color-adjust", "exact", "important");
    }
    var pages = document.querySelectorAll(".pagedjs_page");
    for (var p = 0; p < pages.length; p++) {
      var pageEl = pages[p];
      pageEl.style.setProperty("box-shadow", "none", "important");
      pageEl.style.setProperty("outline", "none", "important");
      pageEl.style.setProperty("overflow", "hidden", "important");
    }
`.trim();
}
